import React, { useState } from 'react';
import { useToast } from '../components/Toast';
import PlanPicker from './PlanPicker';
import { useUserProfile } from './useUserProfile';
import { planLabel } from './planTypes';

const LEVELS = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

/**
 * First-visit gate: plan pick, then a short profile prompt (level + preferred mins)
 * before the wrapped product page.
 */
export default function PlanGate({ children }) {
  const toast = useToast();
  const { uid, profile, planType, loading, error, savePlanType, saveProfileFields } = useUserProfile();
  const [busy, setBusy] = useState(false);
  const [skippedPrefs, setSkippedPrefs] = useState(false);

  if (!uid) return children;

  if (error) {
    return (
      <div className="cs-page yoga-shell">
        <div className="cs-banner b-danger" role="alert">
          Could not load your plan: {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cs-page yoga-shell">
        <p className="cs-help">
          <span className="cs-spinner" aria-hidden /> Loading your plan…
        </p>
      </div>
    );
  }

  if (!planType) {
    const onSelect = async (next) => {
      if (busy) return;
      setBusy(true);
      try {
        await savePlanType(next);
        toast(`Welcome — you are on ${planLabel(next)}`, 'ok');
      } catch (err) {
        toast(`Could not save plan: ${err.message}`, 'danger', 8000);
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="cs-page yoga-shell">
        <PlanPicker
          onSelect={onSelect}
          busy={busy}
          showPrices
          lead="Pick how you want to use YogaKai. You can change this later in Membership. Preview — billing is not connected."
        />
      </div>
    );
  }

  const needsPrefs = profile && !profile.practiceLevel && !skippedPrefs;
  if (needsPrefs) {
    return (
      <ProfilePrompt
        busy={busy}
        setBusy={setBusy}
        saveProfileFields={saveProfileFields}
        onSkip={() => setSkippedPrefs(true)}
        toast={toast}
      />
    );
  }

  return children;
}

function ProfilePrompt({ busy, setBusy, saveProfileFields, onSkip, toast }) {
  const [level, setLevel] = useState('beginner');
  const [mins, setMins] = useState(30);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await saveProfileFields({ practiceLevel: level, preferredMins: mins });
      toast('Preferences saved', 'ok');
    } catch (err) {
      toast(`Could not save: ${err.message}`, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cs-page yoga-shell u-stack m-fade-in" style={{ '--gap': 'var(--sp-5)', maxWidth: 480 }}>
      <header className="yoga-page-head">
        <p className="yoga-eyebrow">YogaKai</p>
        <h1 className="yoga-hero-title">Quick preferences</h1>
        <p className="yoga-hero-lead">
          Optional — helps Today recommend a class length and level. You can edit these anytime in Profile.
        </p>
      </header>
      <div className="yoga-panel">
        <fieldset className="u-stack" style={{ '--gap': '8px', border: 0, padding: 0, margin: 0 }}>
          <legend className="yoga-eyebrow">Practice level</legend>
          <div className="u-cluster" style={{ '--gap': '8px' }}>
            {LEVELS.map((lv) => (
              <button
                key={lv.id}
                type="button"
                className="cs-chip"
                aria-pressed={level === lv.id}
                disabled={busy}
                onClick={() => setLevel(lv.id)}
              >
                {lv.label}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="u-stack" style={{ '--gap': '6px' }}>
          <span className="yoga-eyebrow">Preferred session (minutes)</span>
          <input
            className="cs-input"
            type="number"
            min={5}
            max={120}
            step={5}
            value={mins}
            disabled={busy}
            onChange={(e) => setMins(Number(e.target.value) || 30)}
          />
        </label>
        <div className="u-cluster" style={{ '--gap': '8px' }}>
          <button type="button" className="cs-btn primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
          <button type="button" className="cs-btn cs-btn-ghost" disabled={busy} onClick={onSkip}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
